import type { Component } from 'svelte'
import { buildTree, getRelated } from '$lib/tree'

export { helpPath } from './path'

export type HelpMeta = { title: string; teaser: string; sort: number }

/**
 * A single help doc, derived entirely from its file path under `content/`.
 * `full_slug`/`parent_id` mirror the columns the DB-backed help used, so the
 * generic tree helpers (`buildTree`, `getRelated`) work unchanged.
 */
export type HelpNode = {
  id: string
  parent_id: string | null
  slug: string
  full_slug: string
  title: string
  teaser: string
  sort: number
}

// Eager-glob every doc. The content is static and prerendered, so we resolve
// both `meta` (for nav/SEO/"up next") and the component synchronously — a
// component can't travel through `load` data, it would fail serialization.
const modules = import.meta.glob<{ default: Component; meta: HelpMeta }>(
  './content/**/*.svelte',
  {
    eager: true,
  },
)

/** `./content/product/starting-a-session.svelte` → product/starting-a-session */
const parseKey = (key: string) => {
  const segments = key
    .replace('./content/', '')
    .replace(/\.svelte$/, '')
    .replace(/\/index$/, '') // a folder's index.svelte represents the folder
    .split('/')
  return {
    full_slug: segments.join('/'),
    slug: segments[segments.length - 1] ?? '',
    parent_id: segments.length > 1 ? segments.slice(0, -1).join('/') : null,
  }
}

const componentByFullSlug: Record<string, Component> = {}

const nodes: HelpNode[] = Object.entries(modules)
  .map(([key, module]) => {
    const { full_slug, slug, parent_id } = parseKey(key)
    componentByFullSlug[full_slug] = module.default
    return {
      id: full_slug,
      full_slug,
      slug,
      parent_id,
      title: module.meta.title,
      teaser: module.meta.teaser,
      sort: module.meta.sort,
    }
  })
  .sort((a, b) => a.sort - b.sort || a.title.localeCompare(b.title))

export const helpTree = buildTree(nodes)

export const helpFullSlugs = nodes.map((node) => node.full_slug)

export const getHelpNode = (fullSlug: string) =>
  nodes.find((node) => node.full_slug === fullSlug)

export const getHelpNodesBySlug = (slugs: string[]) =>
  slugs
    .map((slug) => nodes.find((node) => node.slug === slug))
    .filter((node): node is HelpNode => node !== undefined)

export const getNextHelpNode = (fullSlug: string) =>
  getRelated(helpTree, fullSlug, 'next')

export const getHelpComponent = (fullSlug: string): Component | undefined =>
  componentByFullSlug[fullSlug]
